import {
  buildAdminStoredReport,
  downloadAdminStoredReport,
  downloadHealthCheckReport,
  downloadStableSetupReport,
  downloadTransactionItemsReport,
  downloadTransactionReport,
  generateTaxuallyReport,
  downloadRemittanceReport,
  getAdminStoredReport,
  type RemittanceReportParams,
} from '@/apis/reports';
import { toaster } from '@/components/ui/toaster-instance';
import type {
  AdminReportBuildRequest,
  RelatedEntityTypeEnum,
  ReportTypeEnum,
  ReportsRead,
  TransactionReportByProductsParams,
  TransactionReportParams,
} from '@/types/reports';
import { StoredReportStatus } from '@/types/reports';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export function getFilenameFromHeaders(
  headers: { 'content-disposition'?: string },
  fallbackPrefix = 'transaction-report',
  fallbackExtension = 'xlsx'
): string {
  const disposition = headers?.['content-disposition'];
  if (disposition) {
    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
    if (match?.[1]) {
      const raw = match[1].replace(/['"]/g, '');
      return raw.split(/[/\\]/).pop() || raw;
    }
  }
  return `${fallbackPrefix}-${new Date().toISOString().split('T')[0]}.${fallbackExtension}`;
}

async function getErrorMessageFromBlobError(
  error: unknown,
  fallback = 'Failed to generate transaction report'
): Promise<string> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as { detail?: string; message?: string };
      return parsed?.detail ?? parsed?.message ?? fallback;
    } catch {
      return fallback;
    }
  }
  if (data && typeof data === 'object' && 'detail' in data) {
    return (data as { detail?: string }).detail ?? fallback;
  }
  if (data && typeof data === 'object' && 'message' in data) {
    return (data as { message?: string }).message ?? fallback;
  }
  return fallback;
}

export const useDownloadReportMutation = (
  title: string,
  filenamePrefix: string,
  downloadFn: () => Promise<Blob>,
  fileExtension = 'xlsx'
) => {
  return useMutation({
    mutationFn: downloadFn,
    onSuccess: blob => {
      downloadBlob(blob, `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.${fileExtension}`);
      toaster.create({
        title: 'Success',
        description: `${title} downloaded successfully`,
        type: 'success',
      });
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: `Failed to download ${title}. Please try again.`,
        type: 'error',
      });
    },
  });
};

export const useDownloadStableSetupReportMutation = () => {
  return useDownloadReportMutation('Stable Setup Report', 'stable-setup-report', downloadStableSetupReport);
};

export const useDownloadHealthCheckReportMutation = () => {
  return useDownloadReportMutation('Health Check Report', 'health-check-report', downloadHealthCheckReport);
};

export const useDownloadTransactionReportMutation = (
  orgId: string | undefined,
  options?: { onEmailSent?: () => void }
) => {
  return useMutation({
    mutationFn: async ({
      reportType,
      payload,
    }: {
      reportType: string;
      payload: Omit<TransactionReportParams, 'report_type'>;
    }) => {
      if (!orgId) throw new Error('Organization is required');
      const res = await downloadTransactionReport(orgId, reportType, { ...payload, report_type: reportType });
      return {
        status: res.status,
        data: res.data,
        headers: res.headers,
      };
    },
    onSuccess: result => {
      if (result.status === 200 && result.data instanceof Blob && result.data.size > 0) {
        const filename = getFilenameFromHeaders(result.headers);
        downloadBlob(result.data, filename);
        toaster.create({
          title: 'Success',
          description: 'Report downloaded successfully',
          type: 'success',
        });
      } else if (result.status === 202) {
        toaster.create({
          title: 'Report requested',
          description: 'The transaction report will be sent to your email shortly.',
          type: 'success',
        });
        options?.onEmailSent?.();
      } else if (result.status === 200) {
        Sentry.captureMessage('Transaction report 200 with invalid/empty blob', {
          level: 'warning',
          extra: {
            status: result.status,
            dataType: typeof result.data,
            blobSize: result.data instanceof Blob ? result.data.size : undefined,
          },
        });
        toaster.create({
          title: 'Error',
          description: 'Report response was empty or invalid. Please try again.',
          type: 'error',
        });
      }
    },
    onError: async (error: unknown) => {
      const errorMessage = await getErrorMessageFromBlobError(error);
      Sentry.captureException(error, {
        extra: { transactionReport: true, responseMessage: errorMessage },
      });
      toaster.create({
        title: 'Error',
        description: errorMessage,
        type: 'error',
      });
    },
  });
};

export const useDownloadTransactionItemsReportMutation = (
  orgId: string | undefined,
  options?: { onEmailSent?: () => void }
) => {
  return useMutation({
    mutationFn: async (payload: TransactionReportByProductsParams) => {
      if (!orgId) throw new Error('Organization is required');
      return downloadTransactionItemsReport(orgId, payload);
    },
    onSuccess: result => {
      if (result.status === 200 && result.data instanceof Blob && result.data.size > 0) {
        const filename = getFilenameFromHeaders(result.headers, 'transaction-items-report');
        downloadBlob(result.data, filename);
        toaster.create({
          title: 'Success',
          description: 'Report downloaded successfully',
          type: 'success',
        });
      } else if (result.status === 202) {
        toaster.create({
          title: 'Report requested',
          description: 'The transaction items report will be sent to your email shortly.',
          type: 'success',
        });
        options?.onEmailSent?.();
      } else if (result.status === 200) {
        toaster.create({
          title: 'Error',
          description: 'Report response was empty or invalid. Please try again.',
          type: 'error',
        });
      }
    },
    onError: async (error: unknown) => {
      const errorMessage = await getErrorMessageFromBlobError(error, 'Failed to generate transaction items report');
      Sentry.captureException(error, {
        extra: { transactionItemsReport: true, responseMessage: errorMessage },
      });
      toaster.create({
        title: 'Error',
        description: errorMessage,
        type: 'error',
      });
    },
  });
};

export const useGenerateTaxuallyReportMutation = (orgId: string | undefined) => {
  return useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error('Organization is required');
      return generateTaxuallyReport(orgId);
    },
    onSuccess: result => {
      if (result.status === 200) {
        const filename = getFilenameFromHeaders(result.headers);
        downloadBlob(result.data, filename);
        toaster.create({
          title: 'Success',
          description: 'Taxually report downloaded successfully',
          type: 'success',
        });
      } else if (result.status === 202) {
        toaster.create({
          title: 'Report requested',
          description: 'The Taxually report will be sent to your email shortly.',
          type: 'success',
        });
      }
    },
    onError: async (error: unknown) => {
      const errorMessage = await getErrorMessageFromBlobError(error);
      toaster.create({
        title: 'Error',
        description: errorMessage,
        type: 'error',
      });
    },
  });
};

export const ADMIN_STORED_REPORT_STATE_KEY = 'admin_stored_report';

export const STORED_REPORT_POLL_INTERVAL_MS = 3000;

type StoredReportQueryArgs = {
  reportType: ReportTypeEnum;
  relatedEntityType: RelatedEntityTypeEnum;
  relatedEntityId: string;
};

export const adminStoredReportQueryKey = ({ reportType, relatedEntityType, relatedEntityId }: StoredReportQueryArgs) =>
  [ADMIN_STORED_REPORT_STATE_KEY, reportType, relatedEntityType, relatedEntityId] as const;

export const getStoredReportRefetchInterval = (data: ReportsRead | null | undefined): number | false => {
  if (data?.status === StoredReportStatus.IN_PROGRESS) {
    return STORED_REPORT_POLL_INTERVAL_MS;
  }
  return false;
};

export const useGetStoredReportStatusQuery = (args: StoredReportQueryArgs) => {
  return useQuery<ReportsRead | null>({
    queryKey: adminStoredReportQueryKey(args),
    queryFn: async () => {
      try {
        return await getAdminStoredReport(args.reportType, args.relatedEntityType, args.relatedEntityId);
      } catch (err) {
        if ((err as AxiosError)?.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!args.relatedEntityId,
    retry: false,
    refetchInterval: query => getStoredReportRefetchInterval(query.state.data),
  });
};

/**
 * `orgId` is optional because some report types are system-scoped (e.g. billing
 * summaries, health-check exports) and the backend does not require the
 * `x-organization-id` header for those. The API client forwards the header only
 * when `orgId` is set.
 */
type BuildAdminReportMutationVars = StoredReportQueryArgs & {
  orgId?: string;
  queuePayload?: AdminReportBuildRequest['queue_payload'];
};

export const useBuildAdminReportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgId,
      reportType,
      relatedEntityType,
      relatedEntityId,
      queuePayload,
    }: BuildAdminReportMutationVars) =>
      buildAdminStoredReport(
        reportType,
        {
          related_entity_id: relatedEntityId,
          related_entity_type: relatedEntityType,
          queue_payload: queuePayload ?? {},
        },
        orgId
      ),
    onSuccess: (data, vars) => {
      const queryKey = adminStoredReportQueryKey({
        reportType: vars.reportType,
        relatedEntityType: vars.relatedEntityType,
        relatedEntityId: vars.relatedEntityId,
      });
      queryClient.setQueryData<ReportsRead>(queryKey, {
        id: data.id,
        report_type: vars.reportType,
        s3_object_name: null,
        related_entity_id: vars.relatedEntityId,
        related_entity_type: vars.relatedEntityType,
        status: StoredReportStatus.IN_PROGRESS,
      });
      queryClient.invalidateQueries({ queryKey });
      toaster.create({
        title: 'Report build queued',
        description: 'The report is being prepared. This may take a few minutes.',
        type: 'success',
      });
    },
    onError: (error: unknown) => {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toaster.create({
        title: 'Error',
        description: detail ?? 'Failed to queue report build. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useDownloadAdminStoredReportMutation = () => {
  return useMutation({
    mutationFn: ({ reportType, relatedEntityType, relatedEntityId }: StoredReportQueryArgs) =>
      downloadAdminStoredReport(reportType, relatedEntityType, relatedEntityId),
    onSuccess: ({ url, filename }) => {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      // Anchor must be in the DOM for the click to register in Firefox.
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: (error: unknown) => {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toaster.create({
        title: 'Error',
        description: detail ?? 'Failed to download report. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useDownloadRemittanceReportMutation = (orgId: string | undefined) => {
  return useMutation({
    mutationFn: async (params: RemittanceReportParams) => {
      if (!orgId) throw new Error('Organization is required');
      return downloadRemittanceReport(orgId, params);
    },
    onSuccess: result => {
      const filename = getFilenameFromHeaders(result.headers, 'remittance-report', 'csv');
      downloadBlob(result.data, filename);
      toaster.create({
        title: 'Success',
        description: 'Remittance report downloaded successfully',
        type: 'success',
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getErrorMessageFromBlobError(error, 'Failed to generate remittance report');
      toaster.create({
        title: 'Error',
        description: errorMessage,
        type: 'error',
      });
    },
  });
};
